/**
 * imagesDuCorps — ce que l'agent lit des images du CORPS d'un acte.
 *
 * Signalement #2141 (11 octobre 2026) : l'agent ne lisait d'une image que
 * `[image]`. Sur une lettre il a changé le nom du signataire, affirmé qu'il n'y
 * avait « aucune image de signature », et laissé la signature manuscrite d'une
 * autre personne sous le nouveau nom. Ce module rend chaque image du corps
 * lisible (identifiant, taille, placement, bloc porteur), dans l'entrée du bloc
 * et à la place de son `[image]` dans le texte lu.
 *
 * ## Mesuré sur superdoc@2.15.1 (2026-10-12, `mesure-images-du-corps.spec.ts`)
 *
 * - `images.list` rend le corps, les en-têtes et les pieds CONFONDUS. Une image
 *   du corps est celle dont `anchorRef.blockId` est un bloc de la projection du
 *   corps ; `anchorRef.blockId` manque sur certaines images d'en-tête.
 * - `anchorRef.blockId`, le `blockId` d'un bloc de la projection Markdown et le
 *   `source.blockId` d'un diagnostic sont le même identifiant. Un paragraphe de
 *   cellule de tableau est un bloc de la projection ; `blocks.list` ne le liste pas.
 * - `images.list` suit l'ordre du document, y compris entre deux images d'un
 *   même paragraphe dont les relations sont écrites dans l'ordre inverse.
 * - La projection écrit chaque image `MARQUE_DIMAGE` et ajoute un diagnostic
 *   `projection-placeholder-emitted` (`construct:'image'`) dont `output.range`
 *   couvre le BLOC entier, pas la marque.
 * - Une image retirée sous suivi quitte `images.list` et la projection `final`
 *   (son paragraphe s'y lit vide) dès le retrait, avant toute décision.
 */

import { CANVAS_INSPECT_IMAGES_PER_BLOCK_MAX, canvasImageReadLabel, type CanvasInspectImage } from '@hector/shared-types';
import { boundedText, type CanvasMarkdownProjection, type CanvasProjectionBlock } from './canvasProjection';
import { operationDuDocument } from './canvasSnapshot';

/** Ce que la projection Markdown de l'éditeur écrit à la place d'une image. */
export const MARQUE_DIMAGE = '<span data-superdoc-placeholder="image">[image]</span>';

/** Une image telle qu'`images.list` la rend, réduite à ce que l'agent en lit. */
export interface ImageListee extends CanvasInspectImage {
  /** Le bloc qui porte l'image. Absent : l'éditeur ne l'a pas ancrée (vu sur des images d'en-tête). */
  blockId?: string;
}

/** Une image du corps : son bloc porteur est connu, et c'est un bloc de la projection du corps. */
export interface ImageDuCorps extends CanvasInspectImage {
  nodeId: string;
}

const estObjet = (valeur: unknown): valeur is Record<string, unknown> => typeof valeur === 'object' && valeur !== null;

/** Pixels par unité de taille de l'éditeur, à 96 pixels par pouce : un point vaut 1/72 de pouce, un twip 1/20 de point. */
const PIXELS_PAR_UNITE: Readonly<Record<string, number>> = { px: 1, pt: 96 / 72, twip: 96 / 1440 };

function tailleEnPixels(taille: unknown): { width: number; height: number } | undefined {
  if (!estObjet(taille)) return undefined;
  const facteur = PIXELS_PAR_UNITE[typeof taille.unit === 'string' ? taille.unit : 'px'];
  const { width, height } = taille;
  if (facteur === undefined || typeof width !== 'number' || typeof height !== 'number') return undefined;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 0 || height < 0) return undefined;
  return { width: Math.round(width * facteur), height: Math.round(height * facteur) };
}

/**
 * Les images d'une réponse d'`images.list`, dans son ordre.
 *
 * Une entrée sans identifiant, sans placement ou sans taille lisible n'est pas
 * rendue : l'agent ne pourrait ni la viser ni la décrire.
 */
export function lireLesImagesListees(reponse: unknown): ImageListee[] {
  if (!estObjet(reponse) || !Array.isArray(reponse.items)) return [];
  const images: ImageListee[] = [];
  for (const entree of reponse.items) {
    if (!estObjet(entree) || typeof entree.sdImageId !== 'string' || entree.sdImageId === '') continue;
    const proprietes = estObjet(entree.properties) ? entree.properties : {};
    const taille = tailleEnPixels(proprietes.size);
    const placement = proprietes.placement;
    if (taille === undefined || (placement !== 'inline' && placement !== 'floating')) continue;
    const blockId = estObjet(entree.anchorRef) && typeof entree.anchorRef.blockId === 'string' ? entree.anchorRef.blockId : undefined;
    images.push({ imageId: entree.sdImageId, placement, ...taille, ...(blockId === undefined ? {} : { blockId }) });
  }
  return images;
}

const IMAGES_PAR_PAGE = 200;
/** Borne de la lecture : au-delà, la suite de la liste n'est pas lue et ses images restent `[image]`. */
const PAGES_LUES_AU_PLUS = 10;

/**
 * Les entrées brutes d'`images.list`, toutes pages lues. Seule lecture de la
 * liste : une lecture sans page (`list({})`) ne rend que ce que l'éditeur veut
 * bien rendre d'un coup, et une image au-delà y passerait pour absente.
 *
 * @returns `undefined` quand le document n'expose pas `images.list`.
 */
async function entreesListees(doc: unknown): Promise<unknown[] | undefined> {
  // `images` est un proxy appelable : seul `operationDuDocument` y trouve `list`.
  const lister = operationDuDocument(doc, 'images', 'list');
  if (lister === undefined) return undefined;
  const entrees: unknown[] = [];
  for (let page = 0; page < PAGES_LUES_AU_PLUS; page += 1) {
    const reponse = await lister({ offset: page * IMAGES_PAR_PAGE, limit: IMAGES_PAR_PAGE });
    const rendues: unknown[] = estObjet(reponse) && Array.isArray(reponse.items) ? reponse.items : [];
    entrees.push(...rendues);
    const total = estObjet(reponse) && typeof reponse.total === 'number' ? reponse.total : 0;
    if (rendues.length < IMAGES_PAR_PAGE || (page + 1) * IMAGES_PAR_PAGE >= total) break;
  }
  return entrees;
}

/**
 * Toutes les images que l'éditeur liste, corps, en-têtes et pieds confondus.
 *
 * @returns `undefined` quand le document n'expose pas `images.list` : « je ne
 *   sais pas lire les images », à ne pas confondre avec une liste vide.
 */
export async function listerLesImages(doc: unknown): Promise<ImageListee[] | undefined> {
  const entrees = await entreesListees(doc);
  return entrees === undefined ? undefined : lireLesImagesListees({ items: entrees });
}

/**
 * Le chemin du média de chaque image listée (`properties.src`, tel que l'éditeur
 * le publie), par identifiant d'image : il dit quel fichier de l'archive porte
 * les octets, et il change quand le contenu d'une image est remplacé.
 *
 * @returns Une table vide quand le document n'expose pas `images.list`.
 */
export async function mediasDesImages(doc: unknown): Promise<Map<string, string>> {
  const medias = new Map<string, string>();
  for (const entree of await entreesListees(doc) ?? []) {
    if (!estObjet(entree) || typeof entree.sdImageId !== 'string' || !estObjet(entree.properties)) continue;
    const src = entree.properties.src;
    if (typeof src === 'string' && src !== '') medias.set(entree.sdImageId, src);
  }
  return medias;
}

/** Les images dont le bloc porteur est l'un des blocs du corps, dans l'ordre de la liste. */
export function imagesDuCorps(images: readonly ImageListee[], blocsDuCorps: ReadonlySet<string>): ImageDuCorps[] {
  return images.flatMap(({ blockId, ...image }) => (blockId !== undefined && blocsDuCorps.has(blockId) ? [{ ...image, nodeId: blockId }] : []));
}

/**
 * Les images du corps que portent les blocs donnés, celles d'une cellule
 * comprises quand le bloc est un tableau : le paragraphe d'une cellule est un
 * bloc à part de la projection, dont la plage tient dans celle de son tableau.
 */
export function imagesPorteesParLesBlocs(
  projection: CanvasMarkdownProjection,
  images: readonly ImageDuCorps[],
  nodeIds: readonly string[],
): ImageDuCorps[] {
  const plageDe = (nodeId: string): { start: number; end: number } | undefined =>
    projection.blocks.find((bloc) => bloc.identity === 'public' && bloc.nodeId === nodeId)?.outputRange;
  const plages = nodeIds.flatMap((nodeId) => plageDe(nodeId) ?? []);
  return images.filter((image) => {
    if (nodeIds.includes(image.nodeId)) return true;
    const porteur = plageDe(image.nodeId);
    return porteur !== undefined && plages.some((plage) => plage.start <= porteur.start && porteur.end <= plage.end);
  });
}

/** L'image telle que le reçu d'inspection la porte, sans son bloc. */
export function imageInspectee({ imageId, placement, width, height }: CanvasInspectImage): CanvasInspectImage {
  return { imageId, placement, width, height };
}

interface Remplacement { debut: number; fin: number; texte: string }

/**
 * Les `[image]` à nommer : pour chaque bloc, autant de marques dans son texte
 * que d'emplacements annoncés par la projection et que d'images listées, sans
 * quoi le bloc garde ses `[image]`. Associer une marque à une image sur un
 * compte qui ne tombe pas juste ferait viser à l'agent une autre image que
 * celle qu'il lit.
 */
function remplacementsCertains(projection: CanvasMarkdownProjection, images: readonly ImageDuCorps[]): Remplacement[] {
  const parBloc = new Map<string, { debut: number; fin: number; annonces: number }>();
  for (const emplacement of projection.emplacementsDImage) {
    const connu = parBloc.get(emplacement.blockId);
    if (connu === undefined) parBloc.set(emplacement.blockId, { debut: emplacement.debut, fin: emplacement.fin, annonces: 1 });
    else connu.annonces += 1;
  }
  const remplacements: Remplacement[] = [];
  for (const [blockId, { debut, fin, annonces }] of parBloc) {
    const duBloc = images.filter((image) => image.nodeId === blockId);
    const marques: number[] = [];
    for (let rang = projection.content.indexOf(MARQUE_DIMAGE, debut); rang >= 0 && rang + MARQUE_DIMAGE.length <= fin; rang = projection.content.indexOf(MARQUE_DIMAGE, rang + MARQUE_DIMAGE.length)) {
      marques.push(rang);
    }
    if (annonces !== marques.length || annonces !== duBloc.length) continue;
    marques.forEach((rang, index) => remplacements.push({ debut: rang, fin: rang + MARQUE_DIMAGE.length, texte: canvasImageReadLabel(duBloc[index]!) }));
  }
  return remplacements.sort((a, b) => a.debut - b.debut);
}

/**
 * La projection où chaque `[image]` du corps identifiée avec certitude est
 * remplacée par son étiquette, et où chaque bloc porte ses images.
 *
 * Le texte change de longueur : les plages de TOUS les blocs sont recalculées,
 * le backend posant ses repères `⟦#nodeId⟧` à ces positions.
 *
 * @param images Les images listées par l'éditeur ; `undefined` (lecture
 *   impossible) rend la projection telle quelle.
 */
export function nommerLesImagesDeLaProjection(
  projection: CanvasMarkdownProjection,
  images: readonly ImageListee[] | undefined,
): CanvasMarkdownProjection {
  if (images === undefined) return projection;
  const duCorps = imagesDuCorps(images, projection.blocsDuCorps);
  if (duCorps.length === 0) return projection;
  const remplacements = remplacementsCertains(projection, duCorps);

  let content = '';
  let lu = 0;
  for (const remplacement of remplacements) {
    content += projection.content.slice(lu, remplacement.debut) + remplacement.texte;
    lu = remplacement.fin;
  }
  content += projection.content.slice(lu);
  // Une borne de bloc ne tombe jamais dans une marque : seules les marques entièrement avant elle la décalent.
  const deplacer = (position: number): number => remplacements.reduce(
    (somme, remplacement) => (remplacement.fin <= position ? somme + remplacement.texte.length - (remplacement.fin - remplacement.debut) : somme),
    position,
  );
  const blocks = projection.blocks.map((bloc): CanvasProjectionBlock => {
    const outputRange = { start: deplacer(bloc.outputRange.start), end: deplacer(bloc.outputRange.end) };
    const fullText = content.slice(outputRange.start, outputRange.end);
    const { textTruncated: _coupe, ...sansTroncature } = bloc;
    const portees = bloc.identity === 'public'
      ? duCorps.filter((image) => image.nodeId === bloc.nodeId).slice(0, CANVAS_INSPECT_IMAGES_PER_BLOCK_MAX).map(imageInspectee)
      : [];
    return { ...sansTroncature, outputRange, fullText, ...boundedText(fullText), ...(portees.length === 0 ? {} : { images: portees }) };
  });
  return { ...projection, content, blocks };
}
